import { useRef } from "react";

import { CalendarDate, CalendarDateTime, createCalendar } from "@internationalized/date";
import {
  type AriaDatePickerProps,
  type AriaRangeCalendarProps,
  type DateValue,
  type Key,
  useLocale,
  useRangeCalendar,
} from "react-aria";
import {
  type DateFieldState,
  type DateRangePickerState,
  useRangeCalendarState,
} from "react-stately";

import { Button } from "#components/Button";
import { CalendarGrid } from "#components/DatePicker/CalendarGrid";
import { TimeField } from "#components/DatePicker/TimeField";
import { ToggleButton, ToggleButtonGroup } from "#components/ToggleButtonGroup";
import { CaretRightIcon } from "#icons";
import { cn, getRangeByPeriod, type RangePeriod, type TimeZoneOption } from "#utils";

import { NoFocusSelect } from "./NoFocusSelect";
import { TimezoneSelect } from "./TimezoneSelect";

interface RangeCalendarProps<T extends DateValue> extends AriaRangeCalendarProps<T> {
  startFieldProps: AriaDatePickerProps<DateValue>;
  dateFieldStartState: DateFieldState;
  endFieldProps: AriaDatePickerProps<DateValue>;
  dateFieldEndState: DateFieldState;
  yearOptions: number[];
  dateRangePickerState: DateRangePickerState;
  timezoneOptions: TimeZoneOption[];
  timezone: TimeZoneOption | null;
  setTimezone: (tz: TimeZoneOption) => void;
  onPresetSelect?: (label: string) => void;
}

const PERIOD_LABELS: Record<RangePeriod, string> = {
  week: "This Week",
  month: "This Month",
  year: "This Year",
};

export function RangeCalendar<T extends DateValue>({
  dateFieldStartState,
  dateFieldEndState,
  dateRangePickerState,
  timezoneOptions,
  timezone,
  setTimezone,
  yearOptions,
  onPresetSelect,
  ...props
}: RangeCalendarProps<T>) {
  const { locale } = useLocale();
  const state = useRangeCalendarState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef(null);

  const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(props, state, ref);

  const currentMonth = state.focusedDate.month;
  const currentYear = state.focusedDate.year;

  const monthsLabels = Array.from({ length: 12 }, (_, i) => i).map((month) =>
    new Intl.DateTimeFormat(locale, {
      month: "short",
    }).format(new Date(2000, month, 1)),
  );

  const monthOptions = monthsLabels.map((label, i) => ({
    key: i + 1,
    label,
  }));

  function handleMonthChange(key: Key) {
    state.setFocusedDate(new CalendarDate(currentYear, Number(key), 1));
  }

  function handleYearChange(key: Key) {
    state.setFocusedDate(new CalendarDate(Number(key), currentMonth, 1));
  }

  function handleSetRange(period: RangePeriod) {
    const range = getRangeByPeriod(period);
    if (range) {
      dateRangePickerState.setValue({
        start: new CalendarDateTime(range.start.year, range.start.month, range.start.day, 0, 0, 0),
        end: new CalendarDateTime(range.end.year, range.end.month, range.end.day, 23, 59, 59),
      });
      state.setFocusedDate(range.start);
      onPresetSelect?.(PERIOD_LABELS[period]);
    }
  }

  function handleSetTimeRange(minutesAgo: number, label: string) {
    const now = new Date();
    const past = new Date(now.getTime() - minutesAgo * 60 * 1000);

    dateRangePickerState.setValue({
      start: new CalendarDateTime(
        past.getFullYear(),
        past.getMonth() + 1,
        past.getDate(),
        past.getHours(),
        past.getMinutes(),
      ),
      end: new CalendarDateTime(
        now.getFullYear(),
        now.getMonth() + 1,
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
      ),
    });
    onPresetSelect?.(label);
  }

  function handleNextMonth() {
    const maxAvailableMonth =
      state.focusedDate.month === 12 &&
      state.focusedDate.year === yearOptions[yearOptions.length - 1];
    if (!maxAvailableMonth) {
      state.focusNextPage();
    }
  }

  function handlePreviousMonth() {
    const minAvailableMonth =
      state.focusedDate.month === 1 && state.focusedDate.year === yearOptions[0];
    if (!minAvailableMonth) {
      state.focusPreviousPage();
    }
  }

  const hasTime = dateRangePickerState.hasTime;

  // Only show AM/PM buttons if using 12hr cycle time
  const is12HrCycle = [...dateFieldStartState.segments, ...dateFieldEndState.segments].some(
    (seg) => seg.type === "dayPeriod",
  );

  return (
    <div {...calendarProps} className="inline-block w-full" ref={ref}>
      <header className="flex w-full items-center justify-between gap-1 pb-xs">
        <Button
          {...prevButtonProps}
          className="px-0 hover:text-brand-primary"
          onPress={handlePreviousMonth}
          variant="plain"
        >
          <CaretRightIcon className="size-lg rotate-180" />
        </Button>
        <NoFocusSelect onClick={handleMonthChange} options={monthOptions} selected={currentMonth} />
        <NoFocusSelect
          onClick={handleYearChange}
          options={yearOptions.map((y) => ({ key: y, label: y.toString() }))}
          selected={currentYear}
        />

        <Button
          {...nextButtonProps}
          className="px-0 hover:text-brand-primary"
          onPress={handleNextMonth}
          variant="plain"
        >
          <CaretRightIcon className="size-lg" />
        </Button>
      </header>
      <CalendarGrid {...calendarProps} state={state} />
      {hasTime ? (
        <>
          <div className="flex items-center justify-between border-b border-outline-decorative py-xs">
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetRange("week")}
              variant="tertiary"
            >
              This Week
            </Button>
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetRange("month")}
              variant="tertiary"
            >
              This Month
            </Button>
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetRange("year")}
              variant="tertiary"
            >
              This Year
            </Button>
          </div>
          <div className="flex items-center justify-center py-xxs">
            <TimezoneSelect
              onChange={(tz) => setTimezone(tz)}
              selected={timezone}
              timezoneOptions={timezoneOptions}
            />
          </div>
          <div
            className={cn(
              "flex w-full flex-col gap-xxs pb-[1.25rem] pt-lg",
              !dateRangePickerState.dateRange ? "pointer-events-none opacity-50" : "",
            )}
          >
            <div className="grid grid-cols-3 items-center">
              <span className="text-label-small text-text-secondary">Start</span>
              <TimeField {...props.startFieldProps} />
              {is12HrCycle ? (
                <ToggleButtonGroup
                  className="w-fit"
                  onSelectionChange={(keys) => {
                    const value = [...keys][0]?.toString();
                    return value === "AM"
                      ? dateFieldStartState.decrement("dayPeriod")
                      : dateFieldStartState.increment("dayPeriod");
                  }}
                  selectedKeys={[
                    dateFieldStartState.segments
                      .find((s) => s.type === "dayPeriod")
                      ?.text?.toLocaleUpperCase() ?? "AM",
                  ]}
                  selectionMode="single"
                >
                  <ToggleButton className="px-md py-3xs" id="AM" key="AM">
                    AM
                  </ToggleButton>
                  <ToggleButton className="px-md py-3xs" id="PM" key="PM">
                    PM
                  </ToggleButton>
                </ToggleButtonGroup>
              ) : null}
            </div>
            <div className="grid grid-cols-3 items-center">
              <span className="text-label-small text-text-secondary">End</span>
              <TimeField {...props.endFieldProps} />
              {is12HrCycle ? (
                <ToggleButtonGroup
                  className="w-fit"
                  onSelectionChange={(keys) => {
                    const value = [...keys][0]?.toString();
                    return value === "AM"
                      ? dateFieldEndState.decrementPage("dayPeriod")
                      : dateFieldEndState.incrementPage("dayPeriod");
                  }}
                  selectedKeys={[
                    dateFieldEndState.segments
                      .find((s) => s.type === "dayPeriod")
                      ?.text?.toLocaleUpperCase() ?? "AM",
                  ]}
                  selectionMode="single"
                >
                  <ToggleButton className="px-md py-3xs" id="AM" key="AM">
                    AM
                  </ToggleButton>
                  <ToggleButton className="px-md py-3xs" id="PM" key="PM">
                    PM
                  </ToggleButton>
                </ToggleButtonGroup>
              ) : null}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetTimeRange(30, "30m")}
              variant="tertiary"
            >
              30m
            </Button>
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetTimeRange(60, "1h")}
              variant="tertiary"
            >
              1h
            </Button>
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetTimeRange(60 * 6, "6h")}
              variant="tertiary"
            >
              6h
            </Button>
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetTimeRange(60 * 12, "12h")}
              variant="tertiary"
            >
              12h
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-center py-xxs">
            <TimezoneSelect
              onChange={(tz) => setTimezone(tz)}
              selected={timezone}
              timezoneOptions={timezoneOptions}
            />
          </div>
          <div className="flex items-center justify-between pt-xs">
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetRange("week")}
              variant="tertiary"
            >
              This Week
            </Button>
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetRange("month")}
              variant="tertiary"
            >
              This Month
            </Button>
            <Button
              className="px-md py-xxs text-label-medium font-semibold"
              onPress={() => handleSetRange("year")}
              variant="tertiary"
            >
              This Year
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
